import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";

// ✅ ADD THIS
import { motion, AnimatePresence } from "framer-motion";

type LocationId = "salalah" | "muscat";

const ContactPage: React.FC = () => {
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => {
        setContent(data.Contact || {});
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);
  const banners = [
    {
      image: content?.banner_image || "/contact_banner.jpg",
      title: t_api(content?.banner_title || "HOW TO FIND US", content?.banner_title_ar),
      desc: t_api(content?.banner_desc || "Select a location below or send us a message. Our team will respond within 24–48 hours.", content?.banner_desc_ar),
      tag: t_api(content?.banner_tag || "info@hamdanoman.com • Response 24–48h", content?.banner_tag_ar)
    },
  ];

  const locations = useMemo(() => {
    return {
      salalah: {
        city: t_api(content?.salalah_city || "SALALAH – SULTANATE OF OMAN", content?.salalah_city_ar),
        po: t_api(content?.salalah_po || "P.O. Box: 190", content?.salalah_po_ar),
        extra: content?.salalah_extra || "23211499",
        postal: t_api(content?.salalah_postal || "Postal Code: 211", content?.salalah_postal_ar),
        tel: content?.salalah_tel || "+(968) 23210000 / 23211531 / 23211455",
        fax: content?.salalah_fax || "+(968) 23212666",
        mapEmbed: content?.salalah_map || "https://www.google.com/maps?q=Salalah%2C%20Oman&output=embed",
      },
      muscat: {
        city: t_api(content?.muscat_city || "MUSCAT – SULTANATE OF OMAN", content?.muscat_city_ar),
        po: t_api(content?.muscat_po || "P.O.Box: 608 Ruwi", content?.muscat_po_ar),
        extra: content?.muscat_extra || "24501818",
        postal: t_api(content?.muscat_postal || "Postal Code: 112", content?.muscat_postal_ar),
        tel: content?.muscat_tel || "+(968) 24590592 / 24590906",
        fax: content?.muscat_fax || "+(968) 24590549",
        mapEmbed: content?.muscat_map || "https://www.google.com/maps?q=Muscat%2C%20Oman&output=embed",
      },
    };
  }, [content, t_api]);

  const [activeLoc, setActiveLoc] = useState<LocationId>("salalah");
  const active = locations[activeLoc as "salalah" | "muscat"] || Object.values(locations)[0];

  const [form, setForm] = useState({
    name: "",
    email: "",
    contact: "",
    department: "Hamdan Construction",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState<Partial<typeof form>>({});

  const [status, setStatus] = useState<{ type: "idle" | "ok" | "err"; msg: string }>({
    type: "idle",
    msg: "",
  });

  const handleChange =
    (key: keyof typeof form) =>
      (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
      ) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
        // Clear error on change
        setErrors((prev) => ({ ...prev, [key]: "" }));
      };

  const validateForm = (): boolean => {
    const newErrors: Partial<typeof form> = {};

    // Name: letters, spaces, hyphens only
    if (!form.name.trim()) {
      newErrors.name = "Name is required.";
    } else if (!/^[a-zA-Z\u0600-\u06FF\s'-]{2,}$/.test(form.name.trim())) {
      newErrors.name = "Name must contain only letters (min 2 characters).";
    }

    // Email: valid format required
    if (!form.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Phone: optional but if filled must be digits/+/-/spaces
    if (form.contact.trim() && !/^[+\d\s()\-]{6,20}$/.test(form.contact.trim())) {
      newErrors.contact = "Enter a valid phone number (digits, +, -, spaces only).";
    }

    // Subject: min 3 chars
    if (!form.subject.trim()) {
      newErrors.subject = "Subject is required.";
    } else if (form.subject.trim().length < 3) {
      newErrors.subject = "Subject must be at least 3 characters.";
    }

    // Message: min 10 chars
    if (!form.message.trim()) {
      newErrors.message = "Message is required.";
    } else if (form.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: "idle", msg: "" });

    if (!validateForm()) return;

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus({ type: "ok", msg: "✅ Message sent successfully!" });
        setForm({
          name: "",
          email: "",
          contact: "",
          department: content?.form?.departments?.[0]?.name || "Hamdan Construction",
          subject: "",
          message: "",
        });
        setErrors({});
      } else {
        setStatus({ type: "err", msg: "❌ Failed to send. Please try again." });
      }
    } catch {
      setStatus({ type: "err", msg: "❌ Network error. Please try again." });
    }
  };


  // ✅ SIMPLE ANIMATION SETTINGS (no design change)
  const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] }, // ✅ cubic-bezier easeOut
    },
  } as const;

  if (loading) return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;

  return (
    <div className="bg-white text-gray-900" >
      {/* ================= BANNER ================= */}
      <section className="relative h-[70vh]">
        <Swiper
          modules={[EffectFade, Autoplay]}
          effect="fade"
          autoplay={{ delay: 5000 }}
          loop
          className="h-full"
        >
          {banners.map((b: any, i: number) => (
            <SwiperSlide key={i}>
              <div className="relative h-full">
                <img src={b.image} className="h-full w-full object-cover" alt="Contact Banner" />
                <div className="absolute inset-0 bg-black/70" />

                <div className="absolute inset-0 z-10 flex items-center justify-center px-4">
                  {/* ✅ ANIMATE HERO CONTENT */}
                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    className="text-center max-w-4xl"
                  >
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/10 text-white text-xs md:text-sm backdrop-blur">
                      <span className="h-2 w-2 rounded-full bg-yellow-400" />
                      {b.tag}
                    </span>

                    <h1 className="mt-6 text-2xl md:text-5xl font-extrabold text-white">
                      {b.title}
                    </h1>

                    <div className="mx-auto mt-4 h-[3px] w-28 bg-gradient-to-r from-red-600 via-yellow-400 to-red-600 rounded-full" />

                    <p className="mt-5 text-sm md:text-xl text-white/85">{b.desc}</p>
                  </motion.div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* ================= OFFICE CONTACT STRIP ================= */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="-mt-10 relative z-10"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-2xl border bg-white shadow-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-red-700">{t('contact.office_contacts')}</h2>

              <div className="flex gap-2 mt-3 md:mt-0">
                {Object.keys(locations).map((loc) => (
                  <motion.button
                    key={loc}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveLoc(loc as LocationId)}
                    className={`px-5 py-2 rounded-full font-semibold border transition ${activeLoc === loc
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-red-400"
                      }`}
                  >
                    {t(`contact.${loc}`)}
                  </motion.button>
                ))}
              </div>
            </div>

            {active && (
              <div className="grid md:grid-cols-3 gap-6 px-6 py-6">
                <div>
                  <h3 className="font-bold text-gray-800">📍 {active.city}</h3>
                  <p className="mt-2 text-sm text-gray-600">{active.po}</p>
                  <p className="text-sm text-gray-600">{active.extra}</p>
                  <p className="text-sm text-gray-600">{active.postal}</p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800">📞 {t('contact.telephone')}</h3>
                  <p className="mt-2 text-sm text-gray-600">{active.tel}</p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800">📠 {t('contact.fax')}</h3>
                  <p className="mt-2 text-sm text-gray-600">{active.fax}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {/* ================= MAP ================= */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="mt-10"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-2xl overflow-hidden border shadow-lg">
            {active && (
              <iframe src={active.mapEmbed} className="w-full h-[400px]" loading="lazy" title="Office Location" />
            )}
          </div>
        </div>
      </motion.section>

      {/* ================= CONTACT FORM ================= */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="py-16"
      >
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-12 gap-10">
          {/* FORM */}
          <div className="lg:col-span-8">
            <h3 className="text-2xl font-bold text-red-500">{t_api(content?.form?.title || "Send Us Mail")}</h3>

            <form onSubmit={handleSubmit} className="mt-6 grid gap-5" noValidate>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <input
                    type="text"
                    placeholder={t('contact.name')}
                    className={`w-full border rounded-lg px-4 py-3 ${errors.name ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                    value={form.name}
                    onChange={handleChange("name")}
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    placeholder={t('contact.email')}
                    className={`w-full border rounded-lg px-4 py-3 ${errors.email ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                    value={form.email}
                    onChange={handleChange("email")}
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Phone */}
                <div>
                  <input
                    type="tel"
                    placeholder={t('contact.phone')}
                    className={`w-full border rounded-lg px-4 py-3 ${errors.contact ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                    value={form.contact}
                    onChange={handleChange("contact")}
                  />
                  {errors.contact && <p className="mt-1 text-xs text-red-500">{errors.contact}</p>}
                </div>

                {/* Department */}
                <div>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-3"
                    value={form.department}
                    onChange={handleChange("department")}
                  >
                    {(content?.form?.departments || [
                      { name: "Hamdan Construction" },
                      { name: "Hamdan Exchange" },
                      { name: "Hamdan International for Gabbro" },
                      { name: "Hamdan Mining" },
                      { name: "Hamdan Plaza Hotel" },
                      { name: "Hamdan Realestates" },
                      { name: "Hamdan Tires, Battery & Accessories" },
                      { name: "Hamdan Tour And Travel" },
                      { name: "Hamdan Transport" },
                    ]).map((dept: any) => (
                      <option key={dept.name} value={dept.name}>{t_api(dept.name)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Subject */}
              <div>
                <input
                  type="text"
                  placeholder={t('contact.subject')}
                  className={`w-full border rounded-lg px-4 py-3 ${errors.subject ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                  value={form.subject}
                  onChange={handleChange("subject")}
                />
                {errors.subject && <p className="mt-1 text-xs text-red-500">{errors.subject}</p>}
              </div>

              {/* Message */}
              <div>
                <textarea
                  rows={5}
                  placeholder={t('contact.message')}
                  className={`w-full border rounded-lg px-4 py-3 ${errors.message ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                  value={form.message}
                  onChange={handleChange("message")}
                />
                {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
              </div>

              {/* Status Message */}
              <AnimatePresence>
                {status.type !== "idle" && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.25 }}
                    className={`text-sm font-semibold ${status.type === "ok" ? "text-green-600" : "text-red-600"}`}
                  >
                    {status.msg}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              <motion.button
                type="submit"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="w-fit bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700"
              >
                {t('contact.send')}
              </motion.button>
            </form>
          </div>

          {/* INFO */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="lg:col-span-4"
          >
            <div className="border rounded-2xl p-6 bg-white shadow">
              <h3 className="text-xl font-bold text-gray-900">{t_api(content?.bottom_title || "Get in Touch")}</h3>
              <p className="mt-3 text-sm text-gray-600 text-justify">
                {t_api(content?.bottom_desc || "We appreciate your interest in giving to Hamdan Trading Group, and we want to make it as easy as possible for you to get your queries answered. Please contact us in one of the following ways:")}
              </p>

              <div className="mt-4 text-sm text-gray-700 space-y-4">
                <div>
                  <h4 className="font-semibold text-red-500 mb-2">{t('contact.info')}</h4>
                  <div className="space-y-1">
                    <div><strong>{t('contact.address')}:</strong> {t_api(content?.bottom_addr || "Hamdan Headquarter Complex Salalah")}</div>
                    <div><strong>{t('contact.area')}:</strong> {t_api(content?.bottom_area || "190, Oman")}</div>
                    <div><strong>{t('contact.telephone')}:</strong> {content?.bottom_tel || "+(968) 23-210000"}</div>
                    <div><strong>{t('contact.fax')}:</strong> {content?.bottom_fax || "+(968) 23-212666"}</div>
                  </div>
                </div>

                <div className="pt-3 border-t">
                  <h4 className="font-semibold text-red-500 mb-2">{t('contact.support')}</h4>
                  <div className="space-y-1">
                    <div><strong>{t('contact.telephone')}:</strong> {content?.support_tel || "+968 23-211025"}</div>
                    <div>
                      <strong>{t('contact.email')}:</strong>{" "}
                      <a href={`mailto:${content?.support_email || "info@hamdanoman.com"}`} className="text-blue-600 hover:underline">
                        {content?.support_email || "info@hamdanoman.com"}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default ContactPage;
